import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useRouter } from "@tanstack/react-router";

import { authClient } from "../../../features/auth/client";
import { useAssignments } from "../../../features/auth/use-permission";
import { PlatformConsoleScreen } from "../../../features/platform/components/platform-console-screen";
import { reachesPlatformConsole } from "../../../features/platform/platform-access";

/**
 * `/platform` — the platform operator's console (`RPM-064`, `CR-46`, `J-297`). The top bar reads
 * Platform operator here (the shell, `_authenticated.tsx`), and the screen opens with the same words.
 *
 * THE ROUTE COMPOSES: the console is `platform`'s, the session and its assignments are `auth`'s. An
 * account that holds no `PLATFORM_OPERATOR` at `PLATFORM` scope is told so in words; the server
 * refuses it on every route under `/api/rpm/v1/platform/**` whatever the screen showed.
 *
 * IMPERSONATE hands the session to the administrator: everything cached for the operator is dropped,
 * the session is read again — the shell then names whom it acts as and announces it — and the screen
 * moves to the tenant's Readiness, where the administrator works.
 */
function PlatformRoute() {
  const router = useRouter();
  const client = useQueryClient();
  const reaches = reachesPlatformConsole(useAssignments());
  if (!reaches)
    return (
      <p className="pt-4 text-sm font-medium" data-testid="platform-no-access">
        You do not have permission to open the platform console. It is for the platform operator.
      </p>
    );
  return (
    <div className="flex flex-col gap-4 pt-4">
      <PlatformConsoleScreen
        onImpersonated={async () => {
          client.clear();
          authClient.$store.notify("$sessionSignal");
          await router.invalidate();
          await router.navigate({ to: "/admin/readiness" });
        }}
        onOpenEntry={(entryId) =>
          void router.navigate({ to: "/platform/entries/$entryId", params: { entryId } })
        }
      />
    </div>
  );
}

export const Route = createFileRoute("/_authenticated/platform/")({
  // THE PAGE HEADER IS THE SHELL'S: it reads this name and moves focus to its one h1 on arrival.
  staticData: { screenName: "Platform console" },
  component: PlatformRoute,
});
